import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from './ui/Drawer';
import { CURRENCIES } from '../lib/catalogs';
import { useCards, useCreateCard, useRecordCardTx } from '../hooks/useCards';

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

export function CardCreateDrawer({
  wsId,
  open,
  onOpenChange,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateCard(wsId);
  const [provider, setProvider] = useState<'stripe' | 'gmo-pg'>('stripe');
  const [brand, setBrand] = useState('Visa');
  const [last4, setLast4] = useState('');
  const [currency, setCurrency] = useState('JPY');
  const [limitCents, setLimitCents] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setError(null);
    if (last4 && !/^\d{4}$/.test(last4)) {
      setError(t('cards.errorLast4'));
      return;
    }
    try {
      await create.mutateAsync({
        provider,
        brand: brand.trim() || undefined,
        last4: last4 || undefined,
        currency,
        limitCents: limitCents ? Number(limitCents) : undefined,
      });
      onOpenChange(false);
      setLast4('');
      setLimitCents('');
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Create failed');
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t('cards.addCard')}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={create.isPending}
            className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}
        <FormField label={t('cards.provider')}>
          <select value={provider} onChange={(e) => setProvider(e.target.value as 'stripe' | 'gmo-pg')} className={inputCn}>
            <option value="stripe">Stripe Issuing</option>
            <option value="gmo-pg">GMO Payment Gateway</option>
          </select>
        </FormField>
        <FormField label={t('cards.brand')}>
          <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Visa / Mastercard / JCB" className={inputCn} />
        </FormField>
        <FormField label={t('cards.last4')}>
          <input
            value={last4}
            onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="4242"
            className={inputCn}
            inputMode="numeric"
          />
        </FormField>
        <FormField label={t('inbox.field.currency')}>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCn}>
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.symbol}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label={t('cards.limitCents')}>
          <input
            type="number"
            value={limitCents}
            onChange={(e) => setLimitCents(e.target.value)}
            placeholder="100000"
            className={inputCn}
          />
        </FormField>
      </div>
    </Drawer>
  );
}

export function CardTxRecordDrawer({
  wsId,
  open,
  onOpenChange,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const { data: cards } = useCards(wsId);
  const record = useRecordCardTx(wsId);
  const today = new Date().toISOString().slice(0, 10);
  const [cardId, setCardId] = useState('');
  const [occurredAt, setOccurredAt] = useState(today);
  const [merchant, setMerchant] = useState('');
  const [mcc, setMcc] = useState('');
  const [amountCents, setAmountCents] = useState('');
  const [currency, setCurrency] = useState('JPY');
  const [status, setStatus] = useState<'authorized' | 'captured' | 'refunded' | 'disputed'>('captured');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setError(null);
    if (!cardId) {
      setError(t('cards.errorPickCard'));
      return;
    }
    const cents = Number(amountCents);
    if (!Number.isFinite(cents)) {
      setError(t('cards.errorAmount'));
      return;
    }
    try {
      await record.mutateAsync({
        cardId,
        occurredAt,
        merchant: merchant.trim() || undefined,
        mcc: mcc.trim() || undefined,
        amountCents: cents,
        currency,
        status,
      });
      onOpenChange(false);
      setMerchant('');
      setMcc('');
      setAmountCents('');
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Record failed');
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t('cards.recordTransaction')}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={record.isPending}
            className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}
        <FormField label={t('cards.card')}>
          <select value={cardId} onChange={(e) => setCardId(e.target.value)} className={inputCn}>
            <option value="">—</option>
            {(cards ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.brand ?? 'Card'} •••• {c.last4 ?? '0000'} ({c.currency})
              </option>
            ))}
          </select>
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('cards.occurredAt')}>
            <input type="date" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} className={inputCn} />
          </FormField>
          <FormField label={t('cards.status')}>
            <select value={status} onChange={(e) => setStatus(e.target.value as any)} className={inputCn}>
              <option value="authorized">authorized</option>
              <option value="captured">captured</option>
              <option value="refunded">refunded</option>
              <option value="disputed">disputed</option>
            </select>
          </FormField>
        </div>
        <FormField label={t('cards.merchant')}>
          <input value={merchant} onChange={(e) => setMerchant(e.target.value)} className={inputCn} />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('cards.mcc')}>
            <input value={mcc} onChange={(e) => setMcc(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="5812" className={inputCn} />
          </FormField>
          <FormField label={t('cards.amountCents')}>
            <input type="number" value={amountCents} onChange={(e) => setAmountCents(e.target.value)} className={inputCn} />
          </FormField>
        </div>
        <FormField label={t('inbox.field.currency')}>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCn}>
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.symbol}
              </option>
            ))}
          </select>
        </FormField>
      </div>
    </Drawer>
  );
}
